/**
 * The KPI dashboard's row order for this user (DOP-613): Summit's default, or
 * the order they dragged it into, saved on the server so it follows them to
 * any browser.
 *
 * Moves apply at once and save shortly after (debounced — a burst of arrow-key
 * presses is one request, not ten). If the layout cannot be loaded the
 * dashboard keeps the server's own grouping; if a save fails the new order
 * stays on screen and the page says it was not saved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	type KpiLayoutOrder,
	type KpiMetricDrop,
	arrangeKpiTable,
	effectiveOrder,
	knownSectionKeys,
	layoutLabels,
	moveMetric as moveMetricIn,
	moveSection as moveSectionIn,
	toSavedLayout,
	withTableSections,
} from "../components/Kpi/kpiLayout";
import { kpiLayoutService } from "../services/kpiLayoutService";
import type { KpiTable } from "../types/kpi";
import type { KpiLayoutSection, KpiSavedLayout } from "../types/kpiLayout";

const SAVE_DELAY_MS = 500;

export interface KpiLayoutState {
	/** The table in the dashboard's order, with the dashboard's names. */
	arrange: (table: KpiTable) => KpiTable;
	/** Move a metric; `table` is the server's table the grid was drawn from. */
	moveMetric: (table: KpiTable, key: string, drop: KpiMetricDrop) => void;
	moveSection: (
		table: KpiTable,
		key: string,
		drop: { beforeKey?: string | null; afterKey?: string | null },
	) => void;
	/** Back to the default order. */
	reset: () => void;
	/** True when this user has their own order (Reset has something to do). */
	customized: boolean;
	/** The last save did not reach the server. */
	saveFailed: boolean;
}

export function useKpiLayout(): KpiLayoutState {
	const [defaults, setDefaults] = useState<KpiLayoutSection[] | null>(null);
	const [saved, setSaved] = useState<KpiSavedLayout | null>(null);
	const [saveFailed, setSaveFailed] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pending = useRef<KpiSavedLayout | null>(null);

	useEffect(() => {
		let live = true;
		void kpiLayoutService.get().then((layout) => {
			if (!live || !layout) return;
			setDefaults(layout.default);
			setSaved(layout.saved ?? null);
		});
		return () => {
			live = false;
		};
	}, []);

	const flush = useCallback(() => {
		if (timer.current !== null) clearTimeout(timer.current);
		timer.current = null;
		const next = pending.current;
		pending.current = null;
		if (next) {
			void kpiLayoutService.save(next).then((ok) => setSaveFailed(!ok));
		}
	}, []);

	// A move made just before leaving the page still saves.
	useEffect(() => flush, [flush]);

	const order = useMemo<KpiLayoutOrder | null>(
		() => (defaults ? effectiveOrder(defaults, saved) : null),
		[defaults, saved],
	);
	const labels = useMemo(
		() => (defaults ? layoutLabels(defaults) : new Map<string, string>()),
		[defaults],
	);

	const arrange = useCallback(
		(table: KpiTable) => (order ? arrangeKpiTable(table, order, labels) : table),
		[order, labels],
	);

	const known = useMemo(
		() => (defaults ? knownSectionKeys(defaults) : new Set<string>()),
		[defaults],
	);

	const commit = useCallback((next: KpiLayoutOrder) => {
		const layout = toSavedLayout(next, known);
		setSaved(layout);
		pending.current = layout;
		if (timer.current !== null) clearTimeout(timer.current);
		timer.current = setTimeout(flush, SAVE_DELAY_MS);
	}, [flush, known]);

	const moveMetric = useCallback(
		(table: KpiTable, key: string, drop: KpiMetricDrop) => {
			if (!order) return;
			commit(moveMetricIn(withTableSections(order, table), key, drop));
		},
		[order, commit],
	);

	const moveSection = useCallback(
		(
			table: KpiTable,
			key: string,
			drop: { beforeKey?: string | null; afterKey?: string | null },
		) => {
			if (!order) return;
			commit(moveSectionIn(withTableSections(order, table), key, drop));
		},
		[order, commit],
	);

	const reset = useCallback(() => {
		if (timer.current !== null) clearTimeout(timer.current);
		timer.current = null;
		pending.current = null;
		setSaved(null);
		void kpiLayoutService.reset().then((ok) => setSaveFailed(!ok));
	}, []);

	return {
		arrange,
		moveMetric,
		moveSection,
		reset,
		customized: saved !== null,
		saveFailed,
	};
}
